/**
 * useServiceProviderConnector Hook
 *
 * Fetches the full Service Provider connector definition (with the complete
 * `connectionParameterSets`) from the runtime management API. The trimmed
 * `manifest.connector` returned by `/operations/{op}?$expand=properties/manifest`
 * drops auth-provider sets (AAD: Authority/Tenant/CredentialType/Pfx/Password,
 * MSI: Identity), so the creation form needs this fuller payload.
 *
 * Mirrors `StandardConnectionService.getConnector()` in Logic Apps Designer.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import {
  fetchServiceProviderConnector,
  type ServiceProviderConnectorResponse,
} from '@/services/logicAppsApi';
import { extractOperationGroup, isManagedApiConnector } from '@/utils/connections';

const STALE_TIME = 1000 * 60 * 60 * 8; // 8h — connector schema is effectively immutable per version

const QUERY_OPTIONS = {
  staleTime: STALE_TIME,
  gcTime: STALE_TIME,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true as const,
  retry: 2,
};

export function serviceProviderConnectorQueryKey(
  operationGroup: string | null
): ['serviceProviderConnector', string | null] {
  return ['serviceProviderConnector', operationGroup];
}

function resolveOperationGroup(connectorId: string | null): string | null {
  if (!connectorId || isManagedApiConnector(connectorId)) return null;
  return extractOperationGroup(connectorId) || null;
}

export function useServiceProviderConnector(connectorId: string | null) {
  const { getOttoToken } = useAuth();
  const { selectedApp, selectedProject } = useAppStore();

  const operationGroup = useMemo(
    () => resolveOperationGroup(connectorId),
    [connectorId]
  );

  return useQuery<ServiceProviderConnectorResponse | null>({
    queryKey: serviceProviderConnectorQueryKey(operationGroup),
    queryFn: async () => {
      if (!selectedApp || !selectedProject || !operationGroup) return null;
      try {
        return await fetchServiceProviderConnector({
          projectName: selectedProject,
          appName: selectedApp.name,
          getToken: getOttoToken,
          operationGroup,
        });
      } catch (error) {
        // Swallow — caller falls back to the trimmed embedded connector copy.
        console.warn(
          `[useServiceProviderConnector] Failed to fetch full connector for ${operationGroup}:`,
          error
        );
        return null;
      }
    },
    enabled: !!selectedApp && !!selectedProject && !!operationGroup,
    ...QUERY_OPTIONS,
  });
}
